import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { TableInterestPoints } from "@components";
import { Notification } from "@rio-cloud/rio-uikit";
import { useDebounce } from "src/hooks/useDebounce";
import PageContainer from "../../components/routes/PageContainer";
import { deleteInterestPoint, getAllInterestPoints } from "../../services/interestPoints";
import { InterestPoint } from "../../types/interestPoints";

const PAGE_SIZE = 10;
const InterestPointsScreen = () => {
  const navigate = useNavigate();

  const [interestPoints, setInterestPoints] = useState<InterestPoint[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [sort, setSort] = useState("desc");
  const [fieldSort, setFieldSort] = useState("createdAt");
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const debouncedSearch = useDebounce(searchValue, 400);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    loadInterestPointsAndClean(signal, debouncedSearch);

    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    loadMoreInterestPoints();
  }, [searchValue, sort, fieldSort]);

  const loadInterestPointsAndClean = async (signal?: AbortSignal, searchValue?: string) => {
    const { rows, pages } = await searchInterestPoints(1, searchValue || "");
    setInterestPoints(rows);
    setPages(pages);
  };

  const searchInterestPoints = async (page: number, searchValue: string) => {
    try {
      const res = await getAllInterestPoints(searchValue, 0, page === 0 ? PAGE_SIZE : page * PAGE_SIZE);
      return { rows: res.content, pages: res.totalPages };
    } catch (error) {
      Notification.error("Não foi possível buscar os pontos de interesse.");
      return { rows: [], pages: 0 };
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreInterestPoints = async (newPage?: number) => {
    const res = await searchInterestPoints(newPage || page, searchValue);
    setInterestPoints([...interestPoints, ...res.rows]);
  };

  const handlePage = (params: { page: number; pages?: number }) => {
    const nextPage = params.page;
    setPage(nextPage);
    loadMoreInterestPoints(nextPage - 1);
  };

  const handleCreateInterestPoint = () => {
    const url = "/routing/interest-points/create";
    navigate(url, {
      state: {
        edit: false,
      },
    });
  };

  const handleUpdateInterestPoint = (id: number) => {
    const poi = interestPoints.find((i) => i.id === id);
    const url = "/routing/interest-points/edit";
    navigate(url, {
      state: {
        poi,
      },
    });
  };

  const initialData = async (pageToSearch?: number, signal?: AbortSignal, search = debouncedSearch) => {
    try {
      const pageIndex = search.length > 0 ? 0 : pageToSearch ?? page;
      const searchQuery = search.length > 0 ? search : "";

      const res = await getAllInterestPoints(searchQuery, pageIndex, PAGE_SIZE);

      if (!signal?.aborted) {
        setInterestPoints(res.content);
        setPages(res.totalPages);
      }
    } catch (error) {
      if ((error as any).name !== "CanceledError") {
        Notification.error("Não foi possível buscar os pontos de interesse.");
      }
    } finally {
      if (pageToSearch !== undefined) {
        setPage(pageToSearch);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    initialData(undefined, signal);

    return () => {
      controller.abort();
    };
  }, [page, debouncedSearch, sort, fieldSort]);

  const handleDownload = () => { };

  const handleDeleteInterestPoint = async (id: number) => {
    try {
      await deleteInterestPoint(id);
      Notification.info("Ponto de interesse excluído com sucesso!");
      loadInterestPointsAndClean();
    } catch (error) {
      Notification.error("Erro ao excluir ponto de interesse!");
    }
  };

  const handleSort = (dir: string, field: string) => {
    setFieldSort(field);
    setSort(dir);
  };

  return (
    <PageContainer>
      <TableInterestPoints
        isLoading={isLoading}
        showHeader
        pages={pages}
        pageSize={PAGE_SIZE}
        handleDownload={handleDownload}
        handleSort={handleSort}
        interestPoints={interestPoints}
        viewType="TABLE"
        page={page}
        handleCreateInterestPoint={handleCreateInterestPoint}
        handleUpdateInterestPoint={handleUpdateInterestPoint}
        handleSearchValueChange={(text) => setSearchValue(text)}
        handleDeleteInterestPoint={handleDeleteInterestPoint}
        searchValue={searchValue}
        handlePage={handlePage}
      />
    </PageContainer>
  );
};

export default InterestPointsScreen;
